import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { DatePicker, Table, Row, Col, Card, Statistic } from "antd";
import moment from "moment";
import { Context } from "../../context";
import { Fetch } from "../../fetch";
import { handleExportClick } from "../../utils";
import ExportButton from "../ExportButton";

const { Column } = Table;
const dateFormat = "YYYY-MM-DD";

// Filter Components
const DateRangeFilter = ({ value, onChange }) => (
  <Col span={6}>
    <DatePicker.RangePicker
      style={{ width: "100%" }}
      value={value}
      onChange={onChange}
      format={dateFormat}
      allowClear={true}
      placeholder={['Start Date', 'End Date']}
    />
  </Col>
);

const SubscriptionReports = () => {
  const { token, apiUrl } = useContext(Context);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    data: [],
    summary: {},
    pagination: {}
  });

  const [filters, setFilters] = useState({
    date: [moment().startOf("month"), moment().endOf("month")],
    page: 1,
    limit: 10,
  });

  // Memoized date range key for comparison
  const dateKey = useMemo(() => {
    if (!filters.date || !filters.date[0] || !filters.date[1]) return null;
    return `${moment(filters.date[0]).format(dateFormat)}_${moment(filters.date[1]).format(dateFormat)}`;
  }, [filters.date]);

  // Data fetching function with proper memoization
  const fetchData = useCallback(async () => {
    if (!dateKey) return;

    const [startDate, endDate] = dateKey.split('_');

    setLoading(true);
    Fetch(
      `${apiUrl}/reports/user_subscriptions_range?start_date=${startDate}&end_date=${endDate}&page=${filters.page}&limit=${filters.limit}`,
      token,
      (result) => {
        setData({
          data: result?.data || [],
          pagination: result?.pagination || {},
          summary: result?.summary || {}
        });
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching subscriptions data:', error);
        setLoading(false);
      }
    );
  }, [apiUrl, token, filters.page, filters.limit, dateKey]);

  // Initialize data on component mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateChange = (value, dateString) => {
    // Handle cancel/clear case - set to default date range
    if (!value || !dateString || dateString.length === 0) {
      setFilters({
        ...filters,
        date: [moment().startOf("month"), moment().endOf("month")],
        page: 1, // Reset to first page when date changes
      });
      return;
    }

    // Validate dates before setting
    const startDate = moment(dateString[0], dateFormat);
    const endDate = moment(dateString[1], dateFormat);

    if (startDate.isValid() && endDate.isValid()) {
      setFilters({
        ...filters,
        date: [startDate, endDate],
        page: 1, // Reset to first page when date changes
      });
    }
  };

  const handleTableChange = (paginationInfo) => {
    setFilters({
      ...filters,
      page: paginationInfo.current,
      limit: paginationInfo.pageSize,
    });
  };

  return (
    <div className="manage-orders-wrapper">
      <Card>
        <Row style={{ marginBottom: 20 }} align="middle">
          <DateRangeFilter
            value={[
              moment(filters.date[0], dateFormat),
              moment(filters.date[1], dateFormat),
            ]}
            onChange={handleDateChange}
          />
        </Row>

        {/* Summary Statistics Section */}
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col xs={24} sm={8}>
            <Card size="small" loading={loading}>
              <Statistic
                title="New Subscriptions"
                value={data.summary?.total_purchases || 0}
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small" loading={loading}>
              <Statistic
                title="Renewals"
                value={data.summary?.total_renewals || 0}
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small" loading={loading}>
              <Statistic
                title="Total Subscriptions"
                value={(data.summary?.total_purchases || 0) + (data.summary?.total_renewals || 0)}
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
        </Row>

        <div>
          <Table
            dataSource={data.data}
            loading={loading}
            pagination={{
              current: data.pagination.page,
              pageSize: data.pagination.limit,
              total: data.pagination.total,
              showSizeChanger: true,
            }}
            onChange={handleTableChange}
            size="small"
            rowKey="user_id"
          >
            <Column
              title="User ID"
              dataIndex="user_id"
              key="user_id"
              align="center"
              width={150}
              ellipsis
            />
            <Column
              title="User"
              dataIndex="user_str"
              key="user_str"
              align="center"
              width={200}
              ellipsis
              render={(value) => (
                <div style={{ textAlign: "center" }}>
                  {value?.split("\n").map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </div>
              )}
            />
            <Column
              title="Source/Medium"
              dataIndex="utm_data"
              key="utm_data"
              align="center"
              width={150}
              ellipsis
              render={(_, record) =>
                record.utm_data &&
                  record.utm_medium &&
                  record.utm_data !== "NA" &&
                  record.utm_medium !== "NA"
                  ? `${record.utm_data}/${record.utm_medium}`
                  : "NA"
              }
            />
            <Column
              title="Referrer"
              dataIndex="referrer"
              key="referrer"
              align="center"
              width={150}
              ellipsis
              render={(_, record) => (record.referrer ? record.referrer : "NA")}
            />
          </Table>
        </div>

        <ExportButton
          className="export-btn-only"
          onExport={() => {
            const startDate = moment(filters.date[0]).format(dateFormat);
            const endDate = moment(filters.date[1]).format(dateFormat);
            return handleExportClick(
              apiUrl,
              token,
              `user_subscriptions_range?start_date=${startDate}&end_date=${endDate}`,
              `user_subscriptions_${startDate}_${endDate}.csv`
            );
          }}
        />
      </Card>
    </div>
  );
};

export default SubscriptionReports;
